import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  ISourceEntityTypes,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ConditionConfiguration,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import { type IConditionConfiguration } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  asyncForEach,
  multiSortBy$,
  nameSorter,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import Fuse from 'fuse.js';
import { compact, first, sortBy } from 'lodash';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { IBaseMigrationItemCode } from '../interfaces';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { ITEM_CODE_TO_CONDITION_DEFAULT_MAPPINGS } from './ada-code-mappings';
import { ItemCodeToConditionToXSLX } from './item-code-to-condition-to-xlsx';
import { XLSXToItemCodeToCondition } from './xlsx-to-item-code-to-condition';
import { PracticeMigration } from '../practice-migrations';

export const ITEM_CODE_TO_CONDITION_CUSTOM_MAPPING_TYPE =
  'itemCodeToConditionMapping';

export const ITEM_CODE_TO_CONDITION_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Item Code To Condition',
    description: `Used for mapping pre-existing Treatment Item Codes to Conditions in Principle.`,
    type: ITEM_CODE_TO_CONDITION_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Item Code',
    destinationIdentifier: 'Condition Name',
  },
});

export abstract class BaseItemCodeToConditionMappingHandler<
  ItemCode extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<ItemCode>,
> extends BaseCustomMappingHandler<IConditionConfiguration> {
  customMapping = ITEM_CODE_TO_CONDITION_MAPPING;
  abstract translateFn: (record: ItemCode['data']) => IBaseMigrationItemCode;
  abstract sourceEntity: SourceEntity;

  async getItemCodes(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationItemCode[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    return records.map((record) => record.data).map(this.translateFn);
  }

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.getItemCodes(migration);

    const options = records.map((record) => {
      return {
        label: this._buildLabel(record),
        value: record.id.toString(),
      };
    });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IConditionConfiguration>[]> {
    return snapshot(
      ConditionConfiguration.all$(migration.configuration.brand).pipe(
        toINamedDocuments(),
        multiSortBy$(nameSorter())
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, itemCodes, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, itemCodes, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodeToCondition()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const itemCodes = await this.getItemCodes(migration);
    const codeToSourceInfo = new Map<
      string,
      { sourceIdentifier: string; sourceLabel: string }
    >();
    itemCodes.forEach((itemCode) => {
      codeToSourceInfo.set(itemCode.itemCode.trim(), {
        sourceIdentifier: itemCode.id.toString(),
        sourceLabel: this._buildLabel(itemCode),
      });
    });

    const conditionConfigurations = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const code = item.code?.trim();
      if (!code) {
        return;
      }

      const sourceInfo = codeToSourceInfo.get(code);
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;

      if (!sourceInfo && item.mapTo) {
        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: 'Item code not found in source options',
        });
        return;
      }

      if (!sourceInfo || !item.mapTo) {
        return;
      }

      const conditionConfiguration = conditionConfigurations.find(
        (configuration) => configuration.name === item.mapTo
      );

      if (!conditionConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find condition configuration for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: sourceInfo.sourceIdentifier,
          sourceLabel: label,
          reason: `Condition configuration not found: ${item.mapTo}`,
        });
        return;
      }

      return {
        destinationIdentifier: conditionConfiguration.ref,
        sourceIdentifier: sourceInfo.sourceIdentifier,
        sourceLabel: sourceInfo.sourceLabel,
      };
    });

    const results = await this.upsertBulk(
      compact(recordsForUpload),
      translationMap
    );

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...results.failedRecords],
    });
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const itemCodes = await this.getItemCodes(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const records = await this.getRecords(translationMap);

    const mappedItemCodes = records
      .filter((record) => !!record.destinationIdentifier)
      .map((record) => record.sourceIdentifier);
    const unmappedItemCodes = itemCodes.filter(
      (option) => !mappedItemCodes.includes(option.id.toString())
    );

    const recordsForUpload = unmappedItemCodes.map((record) => {
      const suggestedMappingConfigurationName =
        ITEM_CODE_TO_CONDITION_DEFAULT_MAPPINGS[record.itemCode] ?? undefined;

      const matchingConfiguration = destinationOptions.find(
        (configuration) =>
          configuration.name === suggestedMappingConfigurationName
      );

      const label = this._buildLabel(record);

      if (matchingConfiguration) {
        return {
          destinationIdentifier: matchingConfiguration.ref,
          destinationValue: { value: matchingConfiguration.name },
          sourceIdentifier: record.id.toString(),
          sourceLabel: label,
        };
      }

      const fuse = new Fuse(destinationOptions, {
        keys: ['name'],
        includeScore: true,
        threshold: 0.3,
      });
      const matches = fuse.search(label);
      const match = first(matches)?.item;

      return match
        ? {
            destinationIdentifier: match.ref,
            destinationValue: { value: match.name },
            sourceIdentifier: record.id.toString(),
            sourceLabel: label,
          }
        : undefined;
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  protected _prepareExporterItemCodes(
    options: IBaseMigrationItemCode[]
  ): IBaseMigrationItemCode[] {
    return sortBy(options, 'itemCode');
  }

  protected _buildLabel(record: IBaseMigrationItemCode): string {
    return !record.description
      ? record.itemCode
      : `${record.itemCode} - ${record.description}`;
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    itemCodes: IBaseMigrationItemCode[];
    translator: ItemCodeToConditionToXSLX;
  }> {
    const fileName = this.getFileName();
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    const options = records
      .map((record) => record.data)
      .map((record) => this.translateFn(record));

    const itemCodes = this._prepareExporterItemCodes(options);

    const translationMap = new TranslationMapHandler(migration);
    const translator = new ItemCodeToConditionToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap),
      ITEM_CODE_TO_CONDITION_DEFAULT_MAPPINGS
    );
    return { fileName, itemCodes, translator };
  }
}
